import * as fsp from 'fs/promises';
import { Command } from 'commander';
import type { BrandConfig, SetupOptions } from '../branding/types';
import { resolvePaths, resolveProfile, DEFAULT_PROFILE } from '../branding/apply';
import { pair } from '../pairing/device-code';
import { PairingConfigStore } from '../pairing/store';
import { SpendStore, type SpendCapsState } from '../storage/spend-store';

/**
 * `<brand> init`
 *
 * Runs the device-code OAuth flow, persists credentials at
 * `${configDir}/config.json` (mode 0600), seeds `spend-state.json`
 * with the brand's default caps (overridable via flags), and runs any
 * whitelabel `setupSteps`.
 *
 * The web form that renders the pair command bakes its product-level
 * defaults (executor, poll interval, caps) into the rendered string
 * as flags. The CLI accepts them here and forwards them to brand
 * setup steps via `SetupStepContext.setupOptions`, so the post-pair
 * scaffold can write a runtime YAML that matches the user's choices
 * without further prompting (except for `--cwd` when the chosen
 * executor needs one, which brand steps may prompt for interactively).
 */
export function buildInitCommand(brand: BrandConfig): Command {
  return new Command('init')
    .description(`Pair this ${brand.product.title} with Keep and seed local state`)
    .option('--pairing-code <code>', 'Pairing code from the product UI')
    .option('--keep-url <url>', 'Override the Keep API URL from BrandConfig')
    .option('--jobs-url <url>', 'Override the Jobs API URL from BrandConfig')
    .option(
      '--executor <type>',
      `Executor to write into ${brand.product.id}.yaml (e.g. claude-code). Must be in the brand's executor allowlist.`,
    )
    .option('--poll-interval <ms>', 'Run-loop poll interval in milliseconds')
    .option('--daily-cap <tokens>', 'Daily token cap; overrides the brand default')
    .option('--monthly-cap <tokens>', 'Monthly token cap; overrides the brand default')
    .option(
      '--tmp-dir <path>',
      "Base directory for each job's throwaway checkout (defaults to the OS temp dir; set only when that is unsuitable)",
    )
    .option(
      '-p, --profile <name>',
      'Config profile to pair into (isolates this pairing under its own dir; default "default")',
    )
    .action(
      async (options: {
        pairingCode?: string;
        keepUrl?: string;
        jobsUrl?: string;
        executor?: string;
        pollInterval?: string;
        dailyCap?: string;
        monthlyCap?: string;
        tmpDir?: string;
        profile?: string;
      }) => {
        try {
          const profile = resolveProfile(brand, options.profile);
          const setupOptions = parseSetupOptions(brand, options);

          const effectiveBrand: BrandConfig =
            options.keepUrl || options.jobsUrl
              ? {
                  ...brand,
                  keepRegistration: {
                    ...brand.keepRegistration,
                    ...(options.keepUrl ? { keepApiUrl: options.keepUrl } : {}),
                    ...(options.jobsUrl ? { jobsApiUrl: options.jobsUrl } : {}),
                  },
                }
              : brand;

          const paths = resolvePaths(effectiveBrand, profile);
          await fsp.mkdir(paths.configDir, { recursive: true });

          // Pre-pair brand steps
          for (const step of effectiveBrand.setupSteps ?? []) {
            if (step.phase === 'pre-pair') {
              await step.run({
                brand: effectiveBrand,
                configDir: paths.configDir,
                log: (line) => console.log(line),
                setupOptions,
              });
            }
          }

          const pairing = await pair({
            brand: effectiveBrand,
            pairingCode: options.pairingCode,
          });
          const store = new PairingConfigStore(paths.configFile);
          await store.write(pairing);

          // Seed spend state with the brand's default caps, allowing
          // --daily-cap / --monthly-cap to override the per-product
          // defaults.
          const spendStore = new SpendStore(paths.spendStateFile);
          const seedCaps: SpendCapsState = {
            dailyTokens: setupOptions.dailyCap ?? effectiveBrand.defaultCaps.dailyTokens,
            monthlyTokens: setupOptions.monthlyCap ?? effectiveBrand.defaultCaps.monthlyTokens,
            dailyUsd: effectiveBrand.defaultCaps.dailyUsd,
            monthlyUsd: effectiveBrand.defaultCaps.monthlyUsd,
          };
          await spendStore.setCaps(seedCaps);

          // Post-pair brand steps
          for (const step of effectiveBrand.setupSteps ?? []) {
            if (step.phase === 'post-pair') {
              await step.run({
                brand: effectiveBrand,
                configDir: paths.configDir,
                log: (line) => console.log(line),
                setupOptions,
              });
            }
          }

          const profileFlag = profile === DEFAULT_PROFILE ? '' : ` --profile ${profile}`;
          console.log('');
          console.log(`Profile: ${profile}`);
          console.log(`Config:  ${paths.configFile}`);
          console.log(`Audit:   ${paths.auditFile}`);
          console.log(`Spend:   ${paths.spendStateFile}`);
          console.log('');
          console.log(`Run: ${effectiveBrand.product.cliBinary} run${profileFlag}`);
          process.exit(0);
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          console.error(`init failed: ${message}`);
          process.exit(1);
        }
      },
    );
}

function parseSetupOptions(
  brand: BrandConfig,
  raw: {
    executor?: string;
    pollInterval?: string;
    dailyCap?: string;
    monthlyCap?: string;
    tmpDir?: string;
  },
): SetupOptions {
  const out: SetupOptions = {};

  if (raw.executor !== undefined) {
    const allowed = brand.executorAllowlist;
    const wildcard = allowed.includes('*');
    if (!wildcard && !allowed.includes(raw.executor)) {
      throw new Error(
        `--executor=${raw.executor} is not in this brand's allowlist (${allowed.join(', ')})`,
      );
    }
    out.executor = raw.executor;
  }

  if (raw.pollInterval !== undefined) {
    out.pollIntervalMs = requirePositiveInt('--poll-interval', raw.pollInterval);
  }

  if (raw.dailyCap !== undefined) {
    out.dailyCap = requireNonNegativeInt('--daily-cap', raw.dailyCap);
  }

  if (raw.monthlyCap !== undefined) {
    out.monthlyCap = requireNonNegativeInt('--monthly-cap', raw.monthlyCap);
  }

  if (raw.tmpDir !== undefined) {
    out.tmpDir = raw.tmpDir;
  }

  return out;
}

function requirePositiveInt(flag: string, value: string): number {
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) {
    throw new Error(`${flag} must be a positive integer, got "${value}"`);
  }
  return n;
}

function requireNonNegativeInt(flag: string, value: string): number {
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
    throw new Error(`${flag} must be a non-negative integer, got "${value}"`);
  }
  return n;
}
